import React from 'react';
import reduceRight from 'lodash/reduceRight';

/**
 * @typedef WrapperStack
 * @property {<P>(component: React.ComponentType<P>, props: P) => WrapperStack} wraps
 */

/**
 * Provides a 'builder' object for creating a React element comprised of many
 *     wrappers.
 *
 * This is useful for components which need to use many wrapping components
 *     (such as context providers or system components), especially at
 *     the root level of the app.
 * @example
 * function MyComponent() {
 *     return wrapperStack()
 *         .wraps(OuterWrapper)
 *         .wraps(InnerWrapper, { specificProp: 3 })
 *         .wraps(() => (
 *             <Text>Hello world!</Text>
 *         ))
 *         .build();
 * }
 */
export default function wrapperStack() {
    /** @type {{ component: React.ComponentType, props: {} }[]} */
    const layers = [];


    /** @type {WrapperStack} */
    const self = {
        wraps(component, props) {
            layers.push({ component, props });

            return self;
        },

        build() {
            return reduceRight(layers, (children, { component: Wrapper, props }) => (
                <Wrapper {...props}>
                    {children}
                </Wrapper>
            ), null);
        }
    };

    return self;
}